import { Box, Tooltip } from "@mui/material";
import React, { useState } from "react";
import { useHolidays } from "../Hooks/useHolidays";
import { LeaveDay, useFerieApi } from "../Hooks/useFerieApi";
import config from '../Config/environments';
import { useIsAuthenticated } from "@azure/msal-react";

export interface DayProps {
    day: number;
    month?: number;
    year?: number;
    vacation?: LeaveDay[];
    onVacationUpdate?: (updatedVacation: LeaveDay[]) => void;
    activityType?: number;
    isWeekUpdating?: boolean
}

const getHoverStyle = (isWeekend: boolean, isHoliday: boolean, day: number, isUpdating: boolean, isPast?: boolean, activityType?: number) => {
    if (isPast || isWeekend || isHoliday || day === -1 || isUpdating) {
        return {}
    } else if (activityType === config.holidayCode) {
        return {
            backgroundColor: '#D5F1D6',
            color: '#1b5e20'
        }
    } else {
        return {
            backgroundColor: '#FFEEB9',
            color: '#A4662D'
        }
    }
}

const Day: React.FC<DayProps> = ({ year, month, day, vacation, onVacationUpdate, activityType, isWeekUpdating }: DayProps) => {
    const isAuthenticated = useIsAuthenticated();
    
    const holidayService = useHolidays(year || new Date().getFullYear());
    const api = useFerieApi();
    const [isUpdating, setIsUpdating] = useState(false);

    const getDayInfo = () => {
        if (day <= 0 || !month || !year) {
            return { isWeekend: false, isHoliday: false, date: null };
        }

        const date = new Date(year, month - 1, day);
        const isWeekend = date.getDay() === 0 || date.getDay() === 6;
        const isHoliday = holidayService.isHoliday(date);
        const isPast = date < new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
        const isVacation = vacation?.some(v => {
            const [vYear, vMonth, vDay] = v.date.split('-').map(Number);
            return vYear === year && vMonth === month && day === vDay
                && v.code === config.holidayCode
        });
        const isOtherLeave = vacation?.some(v => {
            const [vYear, vMonth, vDay] = v.date.split('-').map(Number);
            return year === vYear && month === vMonth && day === vDay
                && v.code !== config.holidayCode
        });
        return { isWeekend, isHoliday: !!isHoliday, date, isPast, isVacation, isOtherLeave };
    };

    const getDayStyles = () => {
        
        const { isWeekend, isHoliday, isPast, isVacation, isOtherLeave } = getDayInfo();
        const baseStyles = {
            width: 25,
            fontSize: 13,
            paddingTop: 0.05,
            height: 25,
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: 0.2,
            fontWeight: 'normal',
            backgroundColor: isPast ? '#ece4e4' : 'transparent',
            color: 'black',
            borderRadius: 1,
            cursor: 'pointer',
            position: 'relative' as const,
            transition: 'all 0.3s ease', // Add smooth transition
            transform: 'scale(0.95)',
            opacity: 1,
            "&:hover": getHoverStyle(isWeekend, isHoliday, day, isUpdating, isPast, activityType)
        };


        if (day === -1) {
            return {
                ...baseStyles,
                backgroundColor: 'transparent',
                color: 'transparent',
                fontWeight: 'normal',
                borderRadius: 0,
                cursor: 'default',
            };
        }

        if (isHoliday) {
            baseStyles.backgroundColor = '#E38383';
            baseStyles.color = '#ffebee';
            baseStyles.fontWeight = '500';
            baseStyles.borderRadius = 1;
            baseStyles.cursor = "default";
        }

        if (isWeekend) {
            baseStyles.backgroundColor = '#C3A2A2';
            baseStyles.color = '#635b5bff';
            baseStyles.fontWeight = '500';
            baseStyles.borderRadius = 1;
            baseStyles.cursor = "default";
        }
        if(!isAuthenticated){
            baseStyles.cursor = "default";
            return baseStyles;
        }
        if (isVacation) {
            baseStyles.backgroundColor = '#a5d6a7';
            baseStyles.color = '#1b5e20';
        }
        else if (isOtherLeave) {
            baseStyles.backgroundColor = '#ffe082';
            baseStyles.color = '#A4662D';
        }

        if (isPast) {
            baseStyles.opacity = 0.7;
            baseStyles.cursor = "not-allowed";
        }

        if (isUpdating || isWeekUpdating) {
            return {
                ...baseStyles,
                backgroundColor: 'transparent',
                cursor: "default",
                width: "12px",
                height: "12px",
                margin: "7px auto",
                borderRadius: '50%',
                border: '1px solid #1976d2',
                borderTopColor: 'transparent',
                animation: 'spin 1s linear infinite',
                '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' }
                }
            }
        }

        return baseStyles;
    };
    
    const toggleDay = async () => {
        if (!isAuthenticated) return;
        const { isWeekend, isHoliday, isPast } = getDayInfo();
        if (isHoliday || isPast || isWeekend || activityType === undefined)
            return;
        setIsUpdating(true);

        await api.toggleDay(new Date(year!, month! - 1, day), true, activityType).then((result) => {
            if (onVacationUpdate && result) {
                onVacationUpdate(result);
                setIsUpdating(false);
            }
        }).catch((error) => {
            console.error('Error toggling day:', error);
            setIsUpdating(false);
        })
    };

    return (
        <Tooltip title={holidayService.getHolidayName(year ?? 0, month ?? 0, day)} arrow>
            <Box sx={getDayStyles()} onClick={() => { toggleDay() }} >
                {day === -1 || isUpdating || isWeekUpdating ? '' : day}
            </Box>
        </Tooltip>
    );
};
export default Day;